import { useState, Fragment, useMemo, useEffect } from 'react';
import { Formik, FieldArray, Form } from 'formik';
import * as Yup from 'yup';
import {
  Box, Button, Grid, TextField, Typography, Switch, FormControlLabel, Paper, Divider, Chip, Stack,
  MenuItem, Checkbox, FormControl, InputLabel, Select, IconButton, Tooltip,
} from '@mui/material';
import {
  Delete, ArrowBack, Info as InfoIcon, Title, Public, Label, Star,
  WbSunny, DirectionsBus, Cloud, Terrain, LocalActivity, CameraAlt,
  Hotel, QuestionAnswer, Lightbulb, Landscape, SelfImprovement,
} from '@mui/icons-material';
import useSnackbar from '../../hooks/useSnackbar';
import { useDispatch, useSelector } from 'react-redux';
import { addPlaceToStore, updatePlaceInStore, setSelectedPlace } from '../../reduxcomponents/slices/placesSlice';

import _ from 'lodash';
import { insertPlace, updatePlace, getSinglePlace } from '../../api/placeApi';
import { useNavigate, useSearchParams } from 'react-router-dom';

const SECTIONS = [
  { key: 'basic',       label: 'Basic Info',             icon: <InfoIcon sx={{ fontSize: 14 }} />,       color: '#667eea' },
  { key: 'header',      label: 'Header',                 icon: <Title sx={{ fontSize: 14 }} />,          color: '#10b981' },
  { key: 'meta',        label: 'Meta & SEO',             icon: <Public sx={{ fontSize: 14 }} />,         color: '#3b82f6' },
  { key: 'overview',    label: 'Overview & Tags',        icon: <Label sx={{ fontSize: 14 }} />,          color: '#8b5cf6' },

  { key: 'expect',      label: 'What to Expect',         icon: <Star sx={{ fontSize: 14 }} />,           color: '#f43f5e' },
  { key: 'besttime',    label: 'Best Time to Visit',     icon: <WbSunny sx={{ fontSize: 14 }} />,        color: '#f59e0b' },
  { key: 'reach',       label: 'How to Reach',           icon: <DirectionsBus sx={{ fontSize: 14 }} />,  color: '#fb923c' },
  { key: 'weather',     label: 'Weather',                icon: <Cloud sx={{ fontSize: 14 }} />,          color: '#38bdf8' },
  { key: 'geography',   label: 'Geography',              icon: <Terrain sx={{ fontSize: 14 }} />,        color: '#84cc16' },
  { key: 'attractions', label: 'Attractions & Activities', icon: <LocalActivity sx={{ fontSize: 14 }} />, color: '#ec4899' },
  { key: 'experiences', label: 'Experiences',            icon: <SelfImprovement sx={{ fontSize: 14 }} />, color: '#14b8a6' },
  { key: 'nearby',      label: 'Nearby Places',          icon: <Landscape sx={{ fontSize: 14 }} />,      color: '#4ade80' },
  { key: 'accom',       label: 'Accommodations',         icon: <Hotel sx={{ fontSize: 14 }} />,          color: '#a3e635' },
  { key: 'conclusion',  label: 'Conclusions',            icon: <Landscape sx={{ fontSize: 14 }} />,      color: '#94a3b8' },
  { key: 'stays',       label: 'Accommodation Types',    icon: <Hotel sx={{ fontSize: 14 }} />,          color: '#f472b6' },
  { key: 'guidelines',  label: 'Pro Tips',               icon: <Lightbulb sx={{ fontSize: 14 }} />,      color: '#fbbf24' },
  { key: 'faqs',        label: 'FAQs',                   icon: <QuestionAnswer sx={{ fontSize: 14 }} />, color: '#818cf8' },
  { key: 'dynamicInfo', label: 'Additional Info',        icon: <InfoIcon sx={{ fontSize: 14 }} />,       color: '#8b5cf6' },
];

const getInitialValues = (data) => {
  const newData = {
    disabled: false,
    description: '',
    isActive: false,
    tags: [],
    zone: '',
    altitude: '',
    min_temperature: '',
    max_temperature: '',
    price: 0,
    offer_price: 0,
    url: '',
    name: '',
    label: '',
    meta: { title: '', description: '', keywords: [] },
    packages: { description: [""], package_ids: [] },
    expect: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    best_time_to_visit: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], details: [{ months: "", weather: "", footfall: "", catagory: "", best_for: "" }] },
    how_to_reach: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], details: [{ name: "", description: "" }] },
    weather: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], details: [{ season: "", temperature: "", weather: "", best_activity: "" }] },
    geography: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    attraction_and_activites: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], attraction: [{ name: "", description: "" }], activity: [{ name: "", description: "" }] },
    experiences: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], details: [{ name: "", description: "", images: [""] }] },
    nearby_places: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], places: [] },
    accommdations: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], details: [{ category: "", best_stay: "", price_range: "", features: "", best_for: "" }] },
    is_homestay: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], active: false, stays: [""] },
    is_hotel: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], active: false, stays: [""] },
    is_resort: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }], active: false, stays: [""] },
    conclusion: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    guideLines: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    faqs: [{ question: "", answer: "" }],
    bestBeaches: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    famousTemples: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    bestFood: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    whatToBuy: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    famousLocalShopping: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    topMarketPlaces: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    whyChooseEasoTrip: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
    allAbout: { TLDR: "", notes: [""], description: [{ title: "", details: [""] }] },
  };
  if (data) {
    const sanitizedData = JSON.parse(JSON.stringify(data));
    
    // Strip nulls from dynamic object fields so lodash merge doesn't overwrite newData structures with null
    const dynamicFields = [
      'bestBeaches', 'famousTemples', 'bestFood', 'whatToBuy', 
      'famousLocalShopping', 'topMarketPlaces', 'whyChooseEasoTrip', 'allAbout',
      'conclusion', 'is_homestay', 'is_hotel', 'is_resort', 'accommdations',
      'nearby_places', 'experiences', 'attraction_and_activites', 'geography',
      'weather', 'how_to_reach', 'best_time_to_visit', 'expect',
      'guideLines', 'faqs'
    ];
    dynamicFields.forEach(field => {
      if (sanitizedData[field] === null) {
        delete sanitizedData[field];
      }
    });

    if (sanitizedData.is_active !== undefined) {
      sanitizedData.isActive = sanitizedData.is_active;
    }

    return _.merge({}, newData, sanitizedData);
  }

  return newData;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  label: Yup.string().required('label is required'),
})
const SectionWrapper = ({ title, children, id, color = '#667eea' }) => (
  <Box id={id} sx={{ scrollMarginTop: '130px', mb: 3 }}>
    <Paper elevation={0} sx={{
      border: '1px solid #e2e8f0',
      borderRadius: 2,
      overflow: 'hidden',
      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
      transition: 'box-shadow 0.2s',
    }}>
      <Box sx={{
        px: 2.5, py: 1.5,
        background: `linear-gradient(135deg, ${color}18 0%, #ffffff 100%)`,
        borderBottom: `3px solid ${color}`,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{ width: 6, height: 22, borderRadius: 1, bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1e293b', letterSpacing: 0.2 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 2.5 }}>
        {children}
      </Box>
    </Paper>
  </Box>
);

const RenderEditableList = ({ name, values, setFieldValue, label }) => {
  const [inputValue, setInputValue] = useState('');
  const [editIndex, setEditIndex] = useState(null);

  const handleAddOrUpdate = () => {
    const updatedList = [...values];
    if (editIndex !== null) {
      updatedList[editIndex] = inputValue;
      setEditIndex(null);
    } else {
      updatedList.push(inputValue);
    }
    setFieldValue(name, updatedList);
    setInputValue('');
  };

  const handleEdit = (index, event) => {
    event.stopPropagation();
    setInputValue(values[index]);
    setEditIndex(index);
  };

  const handleDelete = (index, event) => {
    event.stopPropagation();
    const updatedList = [...values];
    updatedList.splice(index, 1);
    setFieldValue(name, updatedList);
  };

  return (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={10}>
          <TextField
            label={`Add ${label}`}
            fullWidth
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        </Grid>
        <Grid item xs={2}>
          <Button variant="contained" fullWidth onClick={handleAddOrUpdate}>
            {editIndex !== null ? 'Update' : 'Add'}
          </Button>
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
        {values.map((item, index) => (
          <Chip
            key={index}
            label={item}
            onDelete={(e) => handleDelete(index, e)}
            onClick={(e) => handleEdit(index, e)}
            sx={{ m: 0.5 }}
            deleteIcon={<Delete fontSize="small" />}
          />
        ))}
      </Stack>
    </Box>
  );
};

const SectionStandardFields = ({ basePath, values, handleChange, setFieldValue }) => (
  <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
    <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#475569', fontWeight: 700 }}>Standard Fields (TLDR, Notes, Description)</Typography>
    <TextField label="TLDR (Single Input)" name={`${basePath}.TLDR`} value={_.get(values, `${basePath}.TLDR`, '')} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>Notes (Dynamic Para)</Typography>
    <StringArrayEditor name={`${basePath}.notes`} values={_.get(values, `${basePath}.notes`, [])} handleChange={handleChange} setFieldValue={setFieldValue} label="Note" multiline />
    <Divider sx={{ my: 2 }} />
    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1.5, display: 'block' }}>Description Blocks</Typography>
    <FieldArray name={`${basePath}.description`}>
      {({ push, remove }) => (
        <Grid container spacing={2}>
          {_.get(values, `${basePath}.description`, [])?.map((desc, index) => (
            <Grid item xs={12} key={index} sx={{ mb: 2, p: 2, border: '1px dashed #cbd5e1', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Description Block {index + 1}</Typography>
                <Button onClick={() => remove(index)} color="error" size="small">Remove Block</Button>
              </Box>
              <TextField label="Description Title" name={`${basePath}.description[${index}].title`} value={desc.title || ''} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>Description Details</Typography>
              <StringArrayEditor name={`${basePath}.description[${index}].details`} values={desc.details || []} handleChange={handleChange} setFieldValue={setFieldValue} label="Description Paragraph" multiline />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button variant="outlined" size="small" onClick={() => push({ title: "", details: [""] })}>Add Description Block</Button>
          </Grid>
        </Grid>
      )}
    </FieldArray>
  </Box>
);

const StringArrayEditor = ({ name, values = [], handleChange, setFieldValue, label, multiline = false }) => (
  <FieldArray name={name}>
    {({ push, remove }) => (
      <Box>
        {values.map((val, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
            <TextField label={`${label} ${i + 1}`} name={`${name}[${i}]`} value={val} onChange={handleChange}
              fullWidth size="small" multiline={multiline} minRows={multiline ? 3 : 1} />
            <IconButton onClick={() => remove(i)} color="error" size="small" sx={{ mt: 0.5 }}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button size="small" onClick={() => push('')} sx={{ borderRadius: 2 }}>
          + Add {label}
        </Button>
      </Box>
    )}
  </FieldArray>
);

const PlacesForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { fetchSelectedPlace: selectedPlace } = useSelector((state) => state.place);

  const urlId = searchParams.get('id');

  useEffect(() => {
    if (urlId && (!selectedPlace || selectedPlace._id !== urlId)) {
      getSinglePlace(urlId)
        .then((res) => {
          if (res) dispatch(setSelectedPlace(res));
        })
        .catch((err) => {
          console.error('Failed to load place by id:', err);
        });
    }
  }, [urlId, selectedPlace, dispatch]);

  const activeId = selectedPlace?._id || urlId;

  // Keyed on _id (not the object itself) so an unrelated Redux update that recreates
  // the same place's object reference doesn't reinitialize Formik and wipe unsaved edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialValues = useMemo(() => getInitialValues(selectedPlace), [selectedPlace?._id]);

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const handleSubmit = async (values, { setSubmitting } = {}) => {
    try {
      const payload = { ...values, is_active: values.isActive };
      delete payload.isActive;
      delete payload._id;
      delete payload.__v;
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.created_at;
      delete payload.updated_at;

      // Ensure price and offer_price are valid numbers
      payload.price = payload.price !== '' && payload.price !== undefined ? Number(payload.price) || 0 : 0;
      payload.offer_price = payload.offer_price !== '' && payload.offer_price !== undefined ? Number(payload.offer_price) || 0 : 0;

      // Filter empty string IDs from nearby_places.places if any
      if (payload.nearby_places && Array.isArray(payload.nearby_places.places)) {
        payload.nearby_places.places = payload.nearby_places.places.filter(
          id => id && typeof id === 'string' && id.trim().length === 24
        );
      }

      // Filter empty string IDs from packages.package_ids if any
      if (payload.packages && Array.isArray(payload.packages.package_ids)) {
        payload.packages.package_ids = payload.packages.package_ids.filter(
          id => id && (typeof id === 'object' || (typeof id === 'string' && id.trim().length === 24))
        );
      }

      if (activeId) {
        const res = await updatePlace(payload, activeId); 
        if (res) {
          const updated = res.data || res.place || res;
          dispatch(updatePlaceInStore(updated));
          dispatch(setSelectedPlace(updated));
          showSnackbar('Place updated successfully', 'success');
          navigate(`/places/view`);
        }
      } else {
        const res = await insertPlace(payload); 
        if (res) {
          const created = res.data || res.place || res || values;
          dispatch(addPlaceToStore(created));
          showSnackbar('Place created successfully', 'success');
          navigate(`/places/view`);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errMsg = error.response?.data?.message || error.message || 'Something went wrong';
      showSnackbar(errMsg, 'error');
    } finally {
      if (setSubmitting) setSubmitting(false);
    }
  };

  const Region_obj = [
    {
      label: "None",
      val: "",
    },
    {
      label: "Sikkim",
      val: "sikkim",
    },
    {
      label: "Darjeeling",
      val: "darjeeling",
    },
    // {
    //   label: "North Sikkim",
    //   val: "north-sikkim",
    // },
    {
      label: "Meghalaya",
      val: "meghalaya",
    },
    {
      label: "Arunachal Pradesh",
      val: "arunachal-pradesh",
    },
    {
      label: "Kerala",
      val: "kerala",
    },
    {
      label: "Bhutan",
      val: "bhutan",
    }
  ]


  return (<>
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ values, handleChange, setFieldValue, errors, touched, handleSubmit, isSubmitting, validateForm }) => (
        <Form id="places-form" onSubmit={handleSubmit} style={{ minHeight: '100%' }}>
          <Box sx={{ bgcolor: '#f1f5f9', minHeight: '100%' }}>

            {/* Sticky top bar */}
            <Box sx={{
              position: 'sticky', top: 64, zIndex: 1000,
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              px: 2.5, py: 1.2,
              display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            }}>
              <Tooltip title="Go back">
                <IconButton onClick={() => navigate(-1)} size="small"
                  sx={{ color: '#94a3b8', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <ArrowBack fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 15, mr: 1 }}>
                {activeId ? 'Edit Place' : 'New Place'}
              </Typography>
              {values.name && (
                <Chip label={values.name} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#e2e8f0', fontWeight: 600, fontSize: 12 }} />
              )}
              {values.zone && (
                <Chip label={values.zone} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.25)', color: '#c7d2fe', fontSize: 11 }} />
              )}
              {values.altitude && (
                <Chip label={`${values.altitude}m`} size="small" sx={{ bgcolor: 'rgba(16,185,129,0.2)', color: '#6ee7b7', fontSize: 11 }} />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>Active</Typography>
                <Switch
                  checked={!!values.isActive}
                  onChange={(e) => setFieldValue('isActive', e.target.checked)}
                  size="small"
                  sx={{ '& .MuiSwitch-thumb': { bgcolor: values.isActive ? '#4ade80' : '#94a3b8' } }}
                />
              </Box>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting}
                onClick={async () => {
                  const formErrors = await validateForm();
                  if (formErrors && Object.keys(formErrors).length > 0) {
                    const errorFields = Object.keys(formErrors).join(', ');
                    showSnackbar(`Please fill in required fields: ${errorFields}`, 'error');
                  }
                }}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  fontWeight: 700, px: 3, borderRadius: 2,
                  '&:hover': { background: 'linear-gradient(135deg, #5a67d8, #6b46c1)' },
                }}
              >
                {isSubmitting ? 'Saving...' : (activeId ? 'Update' : 'Create')}
              </Button>
            </Box>

          {/* Body: sidebar + content */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>

            {/* Sidebar */}
            <Box sx={{
              width: 195, flexShrink: 0,
              position: 'sticky', top: 122,
              maxHeight: 'calc(100vh - 130px)', overflowY: 'auto',
              bgcolor: '#fff', borderRight: '1px solid #e2e8f0',
              py: 1.5,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 2 },
            }}>
              <Typography sx={{ px: 2, fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, mb: 1 }}>
                SECTIONS
              </Typography>
              {SECTIONS.map((s) => (
                <Box key={s.key}
                  onClick={() => document.getElementById(`section-${s.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.2,
                    px: 2, py: 0.9, cursor: 'pointer',
                    color: '#475569', fontSize: 12, fontWeight: 500,
                    transition: 'all 0.15s',
                    borderLeft: '3px solid transparent',
                    '&:hover': { bgcolor: `${s.color}15`, color: s.color, borderLeftColor: s.color },
                  }}
                >
                  <Box sx={{ color: 'inherit', display: 'flex' }}>{s.icon}</Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'inherit' }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, p: 3, minWidth: 0 }}>
              <Box>

              <SectionWrapper title="Basic Info" id="section-basic" color="#667eea">
                <Grid container spacing={2}>
                  {/* <Grid item xs={3}><FormControlLabel control={<Switch name="disabled" checked={values.disabled} onChange={handleChange} />} label="Disabled" /></Grid> */}
                  <Grid item xs={3}><FormControlLabel control={<Switch name="isActive" checked={!!values.isActive} onChange={(e) => setFieldValue('isActive', e.target.checked)} />} label="Is Active" /></Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">Region</InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={values.zone}
                        label="Region"
                        onChange={handleChange}
                        name='zone'
                      >
                        {Region_obj.map((obj) => <MenuItem key={obj.val} value={obj.val}>{obj.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Altitude"
                      name="altitude"
                      value={values.altitude}
                      onChange={handleChange}
                      error={touched.altitude && Boolean(errors.altitude)}
                      helperText={touched.altitude && errors.altitude}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Price"
                      name="price"
                      value={values.price}
                      onChange={handleChange}
                      error={touched.price && Boolean(errors.price)}
                      helperText={touched.price && errors.price}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Offer Price"
                      name="offer_price"
                      value={values.offer_price}
                      onChange={handleChange}
                      error={touched.offer_price && Boolean(errors.offer_price)}
                      helperText={touched.offer_price && errors.offer_price}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Min Temperature"
                      name="min_temperature"
                      value={values.min_temperature}
                      onChange={handleChange}
                      error={touched.min_temperature && Boolean(errors.min_temperature)}
                      helperText={touched.min_temperature && errors.min_temperature}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Max Temperature"
                      name="max_temperature"
                      value={values.max_temperature}
                      onChange={handleChange}
                      error={touched.max_temperature && Boolean(errors.max_temperature)}
                      helperText={touched.max_temperature && errors.max_temperature}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="URL"
                      name="url"
                      value={values.url}
                      onChange={handleChange}
                      error={touched.url && Boolean(errors.url)}
                      helperText={touched.url && errors.url}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Label"
                      name="label"
                      value={values.label}
                      onChange={handleChange}
                      error={touched.label && Boolean(errors.label)}
                      helperText={touched.label && errors.label}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      label="Description" 
                      name="description" 
                      value={values.description} 
                      onChange={handleChange} 
                      fullWidth 
                      multiline
                      minRows={3}
                    />
                  </Grid>

                </Grid>
              </SectionWrapper>


              <SectionWrapper title="Meta Data" id="section-meta" color="#3b82f6">
                <Grid container spacing={2}>
                  <Grid item xs={12}><TextField label="Meta Title" name="meta.title" value={values.meta.title} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12}><TextField label="Meta Description" name="meta.description" value={values.meta.description} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12}>
                    <RenderEditableList name="meta.keywords" values={values.meta.keywords} setFieldValue={setFieldValue} label="Meta Keywords" />
                  </Grid>
                </Grid>
              </SectionWrapper>
              
              <SectionWrapper title="Tags" color="#8b5cf6">
                <RenderEditableList name="tags" values={values.tags} setFieldValue={setFieldValue} label="Tag" />
              </SectionWrapper>
              <SectionWrapper title="What To Expect" id="section-expect" color="#f43f5e">
                <SectionStandardFields basePath="expect" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
              </SectionWrapper>
              <SectionWrapper title="Best Time to Visit" id="section-besttime" color="#f59e0b">
                <SectionStandardFields basePath="best_time_to_visit" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />

                {/* Monthly Details */}
                <FieldArray name="best_time_to_visit.details">
                  {({ push, remove }) => (
                    <Grid container spacing={2}>
                      {values.best_time_to_visit.details?.map((item, index) => (
                        <Fragment key={index}>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              name={`best_time_to_visit.details[${index}].months`}
                              label="Months"
                              fullWidth
                              value={item.months}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <TextField
                              name={`best_time_to_visit.details[${index}].weather`}
                              label="Weather"
                              fullWidth
                              value={item.weather}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <TextField
                              name={`best_time_to_visit.details[${index}].footfall`}
                              label="Footfall"
                              fullWidth
                              value={item.footfall}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <TextField
                              name={`best_time_to_visit.details[${index}].catagory`}
                              label="Category"
                              fullWidth
                              value={item.catagory}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <TextField
                              name={`best_time_to_visit.details[${index}].best_for`}
                              label="Best For"
                              fullWidth
                              value={item.best_for}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1} display="flex" alignItems="center">
                            <Button onClick={() => remove(index)} color="error">
                              Remove
                            </Button>
                          </Grid>
                        </Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          onClick={() => push({ months: "", weather: "", footfall: "", catagory: "", best_for: "" })}
                        >
                          Add Time Detail
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </FieldArray>
              </SectionWrapper>
              <SectionWrapper title="How to Reach" id="section-reach" color="#fb923c">
                <SectionStandardFields basePath="how_to_reach" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />

                {/* Details List (Name + Description) */}
                <FieldArray name="how_to_reach.details">
                  {({ push, remove }) => (
                    <Grid container spacing={2}>
                      {values.how_to_reach.details?.map((item, index) => (
                        <Fragment key={index}>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              name={`how_to_reach.details[${index}].name`}
                              label="Name"
                              fullWidth
                              value={item.name}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name={`how_to_reach.details[${index}].description`}
                              label="Description"
                              fullWidth
                              multiline
                              minRows={2}
                              value={item.description}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1} display="flex" alignItems="center">
                            <Button onClick={() => remove(index)} color="error">
                              Remove
                            </Button>
                          </Grid>
                        </Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          onClick={() => push({ name: "", description: "" })}
                        >
                          Add How to Reach Option
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </FieldArray>
              </SectionWrapper>
              <SectionWrapper title="Weather Information" id="section-weather" color="#38bdf8">
                <SectionStandardFields basePath="weather" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />

                {/* Weather Details Grid */}
                <FieldArray name="weather.details">
                  {({ push, remove }) => (
                    <Grid container spacing={2}>
                      {values.weather?.details?.map((entry, index) => (
                        <Fragment key={index}>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              name={`weather.details[${index}].season`}
                              label="Season"
                              fullWidth
                              value={entry.season}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              name={`weather.details[${index}].temperature`}
                              label="Temperature"
                              fullWidth
                              value={entry.temperature}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              name={`weather.details[${index}].weather`}
                              label="Weather"
                              fullWidth
                              value={entry.weather}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <TextField
                              name={`weather.details[${index}].best_activity`}
                              label="Best Activity"
                              fullWidth
                              value={entry.best_activity}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1} display="flex" alignItems="center">
                            <Button onClick={() => remove(index)} color="error">
                              Remove
                            </Button>
                          </Grid>
                        </Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          onClick={() => push({ season: "", temperature: "", weather: "", best_activity: "" })}
                        >
                          Add Weather Detail
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </FieldArray>
              </SectionWrapper>
              <SectionWrapper title="Geography" id="section-geography" color="#84cc16">
                <SectionStandardFields basePath="geography" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
              </SectionWrapper>
              <SectionWrapper title="Attractions and Activities" id="section-attractions" color="#ec4899">
                <SectionStandardFields basePath="attraction_and_activites" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />

                {/* Attractions */}
                <Typography variant="subtitle1" mt={2}>Attractions</Typography>
                <FieldArray name="attraction_and_activites.attraction">
                  {({ push, remove }) => (
                    <Grid container spacing={2}>
                      {values.attraction_and_activites?.attraction?.map((item, index) => (
                        <Fragment key={index}>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              name={`attraction_and_activites.attraction[${index}].name`}
                              label="Attraction Name"
                              fullWidth
                              value={item.name}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name={`attraction_and_activites.attraction[${index}].description`}
                              label="Description"
                              fullWidth
                              multiline
                              minRows={2}
                              value={item.description}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1} display="flex" alignItems="center">
                            <Button onClick={() => remove(index)} color="error">Remove</Button>
                          </Grid>
                        </Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button variant="outlined" onClick={() => push({ name: "", description: "" })}>
                          Add Attraction
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </FieldArray>

                {/* Activities */}
                <Typography variant="subtitle1" mt={2}>Activities</Typography>
                <FieldArray name="attraction_and_activites.activity">
                  {({ push, remove }) => (
                    <Grid container spacing={2}>
                      {values.attraction_and_activites?.activity?.map((item, index) => (
                        <Fragment key={index}>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              name={`attraction_and_activites.activity[${index}].name`}
                              label="Activity Name"
                              fullWidth
                              value={item.name}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name={`attraction_and_activites.activity[${index}].description`}
                              label="Description"
                              fullWidth
                              multiline
                              minRows={2}
                              value={item.description}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1} display="flex" alignItems="center">
                            <Button onClick={() => remove(index)} color="error">Remove</Button>
                          </Grid>
                        </Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button variant="outlined" onClick={() => push({ name: "", description: "" })}>
                          Add Activity
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </FieldArray>
              </SectionWrapper>
              <SectionWrapper title="Experiences" id="section-experiences" color="#14b8a6">
                <SectionStandardFields basePath="experiences" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />

                {/* Experience Details Array */}
                <FieldArray name="experiences.details">
                  {({ push, remove }) => (
                    <Grid container spacing={2}>
                      {values.experiences?.details?.map((item, index) => (
                        <Fragment key={index}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              name={`experiences.details[${index}].name`}
                              label="Experience Title"
                              fullWidth
                              value={item.name}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name={`experiences.details[${index}].description`}
                              label="Experience Description"
                              multiline
                              minRows={2}
                              fullWidth
                              value={item.description}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2} display="flex" alignItems="center">
                            <Button onClick={() => remove(index)} color="error">
                              Remove
                            </Button>
                          </Grid>

                          {/* Image Links Array */}
                          <Grid item xs={12}>
                            <FieldArray name={`experiences.details[${index}].images`}>
                              {({ push: pushImg, remove: removeImg }) => (
                                <Grid container spacing={1}>
                                  {item.images?.map((img, imgIdx) => (
                                    <Grid item xs={12} sm={6} key={imgIdx}>
                                      <TextField
                                        fullWidth
                                        label={`Image ${imgIdx + 1}`}
                                        value={img}
                                        onChange={(e) =>
                                          setFieldValue(`experiences.details[${index}].images[${imgIdx}]`, e.target.value)
                                        }
                                        InputProps={{
                                          endAdornment: (
                                            <Button onClick={() => removeImg(imgIdx)} color="error">
                                              Remove
                                            </Button>
                                          )
                                        }}
                                      />
                                    </Grid>
                                  ))}
                                  <Grid item xs={12}>
                                    <Button variant="outlined" onClick={() => pushImg("")}>
                                      Add Image
                                    </Button>
                                  </Grid>
                                </Grid>
                              )}
                            </FieldArray>
                          </Grid>
                        </Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button variant="outlined" onClick={() => push({ name: "", description: "", images: [""] })}>
                          Add Experience
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </FieldArray>
              </SectionWrapper>
              
              <SectionWrapper title="Nearby Places" id="section-nearby" color="#4ade80">
                <SectionStandardFields basePath="nearby_places" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#475569', fontWeight: 700 }}>Places Reference (Object IDs)</Typography>
                    <RenderEditableList name="nearby_places.places" values={values.nearby_places?.places || []} setFieldValue={setFieldValue} label="Object ID" />
                  </Grid>
                </Grid>
              </SectionWrapper>

              <SectionWrapper title="Accommodations" id="section-accom" color="#a3e635">
                <SectionStandardFields basePath="accommdations" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                <FieldArray name="accommdations.details">
                  {({ push, remove }) => (
                    <Grid container spacing={2}>
                      {values.accommdations.details?.map((item, index) => (
                        <Fragment key={index}>
                          <Grid item xs={12} sm={2}>
                            <TextField
                              name={`accommdations.details[${index}].category`}
                              label="Category"
                              fullWidth
                              value={item.category}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <TextField
                              name={`accommdations.details[${index}].best_stay`}
                              label="Best Stay"
                              fullWidth
                              value={item.best_stay}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <TextField
                              name={`accommdations.details[${index}].price_range`}
                              label="Price Range"
                              fullWidth
                              value={item.price_range}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              name={`accommdations.details[${index}].features`}
                              label="Features"
                              fullWidth
                              value={item.features}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <TextField
                              name={`accommdations.details[${index}].best_for`}
                              label="Best For"
                              fullWidth
                              value={item.best_for}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1} display="flex" alignItems="center">
                            <Button onClick={() => remove(index)} color="error">
                              Remove
                            </Button>
                          </Grid>
                        </Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          onClick={() =>
                            push({
                              category: "",
                              best_stay: "",
                              price_range: "",
                              features: "",
                              best_for: ""
                            })
                          }
                        >
                          Add Accommodation Detail
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </FieldArray>
              </SectionWrapper>

              <SectionWrapper title="Conclusions" id="section-conclusion" color="#94a3b8">
                <SectionStandardFields basePath="conclusion" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
              </SectionWrapper>
              <SectionWrapper title="Accommodation Types" id="section-stays" color="#f472b6">
                {/* Homestay */}
                <Typography variant="subtitle1" gutterBottom>Homestay</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="is_homestay.active"
                          checked={values.is_homestay?.active || false}
                          onChange={handleChange}
                        />
                      }
                      label="Is Active"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <SectionStandardFields basePath="is_homestay" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  </Grid>

                  {/* Homestay Stays */}
                  <Grid item xs={12}>
                    <FieldArray name="is_homestay.stays">
                      {({ push, remove }) => (
                        <Grid container spacing={2}>
                          {values.is_homestay?.stays?.map((stay, idx) => (
                            <Grid item xs={12} sm={6} key={idx}>
                              <TextField
                                fullWidth
                                label={`Homestay Stay ${idx + 1}`}
                                value={stay}
                                onChange={(e) =>
                                  setFieldValue(`is_homestay.stays[${idx}]`, e.target.value)
                                }
                                InputProps={{
                                  endAdornment: (
                                    <Button onClick={() => remove(idx)} color="error">Remove</Button>
                                  ),
                                }}
                              />
                            </Grid>
                          ))}
                          <Grid item xs={12}>
                            <Button variant="outlined" onClick={() => push("")}>
                              Add Homestay Stay
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </FieldArray>
                  </Grid>
                </Grid>

                {/* Hotel */}
                <Typography variant="subtitle1" mt={3} gutterBottom>Hotel</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="is_hotel.active"
                          checked={values.is_hotel?.active || false}
                          onChange={handleChange}
                        />
                      }
                      label="Is Active"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <SectionStandardFields basePath="is_hotel" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  </Grid>

                  {/* Hotel Stays */}
                  <Grid item xs={12}>
                    <FieldArray name="is_hotel.stays">
                      {({ push, remove }) => (
                        <Grid container spacing={2}>
                          {values.is_hotel?.stays?.map((stay, idx) => (
                            <Grid item xs={12} sm={6} key={idx}>
                              <TextField
                                fullWidth
                                label={`Hotel Stay ${idx + 1}`}
                                value={stay}
                                onChange={(e) =>
                                  setFieldValue(`is_hotel.stays[${idx}]`, e.target.value)
                                }
                                InputProps={{
                                  endAdornment: (
                                    <Button onClick={() => remove(idx)} color="error">Remove</Button>
                                  ),
                                }}
                              />
                            </Grid>
                          ))}
                          <Grid item xs={12}>
                            <Button variant="outlined" onClick={() => push("")}>
                              Add Hotel Stay
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </FieldArray>
                  </Grid>
                </Grid>

                {/* Resort */}
                <Typography variant="subtitle1" mt={3} gutterBottom>Resort</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="is_resort.active"
                          checked={values.is_resort?.active || false}
                          onChange={handleChange}
                        />
                      }
                      label="Is Active"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <SectionStandardFields basePath="is_resort" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  </Grid>

                  {/* Resort Stays */}
                  <Grid item xs={12}>
                    <FieldArray name="is_resort.stays">
                      {({ push, remove }) => (
                        <Grid container spacing={2}>
                          {values.is_resort?.stays?.map((stay, idx) => (
                            <Grid item xs={12} sm={6} key={idx}>
                              <TextField
                                fullWidth
                                label={`Resort Stay ${idx + 1}`}
                                value={stay}
                                onChange={(e) =>
                                  setFieldValue(`is_resort.stays[${idx}]`, e.target.value)
                                }
                                InputProps={{
                                  endAdornment: (
                                    <Button onClick={() => remove(idx)} color="error">Remove</Button>
                                  ),
                                }}
                              />
                            </Grid>
                          ))}
                          <Grid item xs={12}>
                            <Button variant="outlined" onClick={() => push("")}>
                              Add Resort Stay
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </FieldArray>
                  </Grid>
                </Grid>
              </SectionWrapper>
              <SectionWrapper title="Pro Tips (Guidelines)" id="section-guidelines" color="#fbbf24">
                <SectionStandardFields basePath="guideLines" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
              </SectionWrapper>
              <SectionWrapper title="Frequently Asked Questions (FAQs)" id="section-faqs" color="#818cf8">
                <FieldArray name="faqs">
                  {({ push, remove }) => (
                    <Grid container spacing={2}>
                      {values.faqs?.map((faq, index) => (
                        <Fragment key={index}>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              name={`faqs[${index}].question`}
                              label={`Question ${index + 1}`}
                              fullWidth
                              value={faq.question}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name={`faqs[${index}].answer`}
                              label="Answer"
                              multiline
                              minRows={2}
                              fullWidth
                              value={faq.answer}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1} display="flex" alignItems="center">
                            <Button onClick={() => remove(index)} color="error">Remove</Button>
                          </Grid>
                        </Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          onClick={() => push({ question: "", answer: "" })}
                        >
                          Add FAQ
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </FieldArray>
              </SectionWrapper>
              
              <SectionWrapper title="Additional Information" id="section-dynamicInfo" color="#8b5cf6">
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>🏖️ Best Beaches & Coastal Escapes to Pair with Your Trip</Typography>
                  <SectionStandardFields basePath="bestBeaches" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  <Divider sx={{ my: 2.5 }} />

                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>{`🛕 Famous Temples & Spiritual Places to Visit in ${values.name || 'Destination'}`}</Typography>
                  <SectionStandardFields basePath="famousTemples" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  <Divider sx={{ my: 2.5 }} />

                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>{`🍔 Best Food to Eat in ${values.name || 'Destination'}`}</Typography>
                  <SectionStandardFields basePath="bestFood" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  <Divider sx={{ my: 2.5 }} />

                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>{`🛍️ What to Buy in ${values.name || 'Destination'}`}</Typography>
                  <SectionStandardFields basePath="whatToBuy" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  <Divider sx={{ my: 2.5 }} />

                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>🎁 Famous Local Shopping Items</Typography>
                  <SectionStandardFields basePath="famousLocalShopping" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  <Divider sx={{ my: 2.5 }} />

                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>{`🏪 Top Market Places & Shopping Streets in ${values.name || 'Destination'}`}</Typography>
                  <SectionStandardFields basePath="topMarketPlaces" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  <Divider sx={{ my: 2.5 }} />

                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>{`⭐ Why Choose EasoTrip for Your ${values.name || 'Trip'}`}</Typography>
                  <SectionStandardFields basePath="whyChooseEasoTrip" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                  <Divider sx={{ my: 2.5 }} />

                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>{`📖 All About ${values.name || 'Destination'}`}</Typography>
                  <SectionStandardFields basePath="allAbout" values={values} handleChange={handleChange} setFieldValue={setFieldValue} />
                </Box>
              </SectionWrapper>
            </Box>
            </Box>
          </Box>
        </Box>
      </Form>
    )}
  </Formik>
    <SnackbarComponent />
  </>

  );
};

export default PlacesForm;